import { Link } from 'react-router-dom';

type ContentBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'group'; title: string; blocks: ContentBlock[] };

const sections: { title: string; blocks: ContentBlock[] }[] = [
  {
    title: '1. Agreement to Terms',
    blocks: [
      { type: 'paragraph', text: 'Welcome to AppGallop.' },
      {
        type: 'paragraph',
        text: 'These Terms of Service ("Terms") govern your access to and use of the websites, applications, marketplaces, platforms, products, APIs, and services operated by AppGallop Pvt. Ltd. ("AppGallop", "we", "our", or "us"), including the AppGallop Marketplace (collectively, the "Services").',
      },
      {
        type: 'paragraph',
        text: 'By accessing, registering for, purchasing from, or using the Services, you acknowledge that you have read, understood, and agree to be legally bound by these Terms and our Privacy Policy.',
      },
      { type: 'paragraph', text: 'If you do not agree to these Terms, you must discontinue use of the Services immediately.' },
    ],
  },
  {
    title: '2. About AppGallop Marketplace',
    blocks: [
      {
        type: 'paragraph',
        text: 'AppGallop Marketplace is a digital platform that enables organizations to discover, evaluate, purchase, manage, and integrate software applications, services, and digital solutions provided by AppGallop and third-party vendors.',
      },
      { type: 'paragraph', text: 'The Marketplace may include:' },
      {
        type: 'list',
        items: ['SaaS applications', 'Business solutions', 'Integrations', 'APIs', 'Cloud services', 'Professional services', 'Partner offerings'],
      },
      {
        type: 'paragraph',
        text: 'Certain products available through the Marketplace are provided by independent third-party providers ("Developers"). AppGallop acts as a platform provider and facilitator unless explicitly stated otherwise.',
      },
    ],
  },
  {
    title: '3. Eligibility',
    blocks: [
      { type: 'paragraph', text: 'To use the Services, you must:' },
      {
        type: 'list',
        items: [
          'Be at least 18 years of age.',
          'Have the legal capacity to enter into binding agreements.',
          'Not be prohibited from using the Services under applicable laws.',
          'Not have been previously suspended or terminated by AppGallop.',
        ],
      },
      {
        type: 'paragraph',
        text: 'If you access the Services on behalf of a company or organization, you represent and warrant that you have authority to bind that organization to these Terms.',
      },
    ],
  },
  {
    title: '4. Account Registration',
    blocks: [
      { type: 'paragraph', text: 'Certain features require account registration.' },
      { type: 'paragraph', text: 'You agree to:' },
      {
        type: 'list',
        items: [
          'Provide accurate, complete, and current information.',
          'Maintain and promptly update account details.',
          'Keep your credentials secure and confidential.',
          'Notify us immediately of unauthorized access or suspected security incidents.',
        ],
      },
      { type: 'paragraph', text: 'You remain responsible for all activities conducted through your account.' },
    ],
  },
  {
    title: '5. Marketplace Services',
    blocks: [
      {
        type: 'paragraph',
        text: 'Subject to these Terms, AppGallop grants you a limited, non-exclusive, non-transferable, revocable license to access and use the Services for legitimate business purposes.',
      },
      { type: 'paragraph', text: 'You may:' },
      {
        type: 'list',
        items: [
          'Browse marketplace listings',
          'Purchase subscriptions',
          'Manage applications and licenses',
          'Access integrations and APIs',
          'Administer authorized users within your organization',
        ],
      },
      { type: 'paragraph', text: 'All rights not expressly granted remain reserved by AppGallop.' },
    ],
  },
  {
    title: '6. Third-Party Applications',
    blocks: [
      {
        type: 'paragraph',
        text: 'Applications offered through the Marketplace may be developed, operated, and maintained by third-party providers.',
      },
      { type: 'paragraph', text: 'AppGallop does not:' },
      {
        type: 'list',
        items: [
          'Own all third-party applications',
          'Control developer operations',
          'Guarantee functionality or availability',
          'Assume responsibility for developer content, support, or warranties',
        ],
      },
      {
        type: 'paragraph',
        text: "Your use of third-party applications is governed by the applicable developer's terms, policies, and agreements.",
      },
      { type: 'paragraph', text: 'Any disputes relating to third-party applications must be addressed directly with the relevant developer.' },
    ],
  },
  {
    title: '7. Orders, Subscriptions & Payments',
    blocks: [
      {
        type: 'group',
        title: 'Purchases',
        blocks: [
          {
            type: 'paragraph',
            text: 'By purchasing any product, subscription, or service through the Marketplace, you authorize AppGallop or its payment processors to charge applicable fees.',
          },
        ],
      },
      {
        type: 'group',
        title: 'Subscription Billing',
        blocks: [
          { type: 'paragraph', text: 'Subscription-based services may renew automatically unless canceled before the renewal date.' },
          { type: 'paragraph', text: 'You are responsible for:' },
          {
            type: 'list',
            items: ['Maintaining valid payment information', 'Reviewing billing details', 'Paying all applicable charges'],
          },
        ],
      },
      {
        type: 'group',
        title: 'Taxes',
        blocks: [
          {
            type: 'paragraph',
            text: 'Unless otherwise stated, fees exclude applicable taxes, duties, levies, or governmental charges, which remain your responsibility.',
          },
        ],
      },
    ],
  },
  {
    title: '8. Refund Policy',
    blocks: [
      { type: 'paragraph', text: 'Except where required by applicable law:' },
      {
        type: 'list',
        items: [
          'Marketplace purchases are non-refundable.',
          'Subscription fees are non-refundable.',
          'Third-party developers may maintain separate refund policies.',
        ],
      },
      { type: 'paragraph', text: 'Please review the applicable product terms before completing a purchase.' },
    ],
  },
  {
    title: '9. Acceptable Use',
    blocks: [
      { type: 'paragraph', text: 'You agree not to:' },
      {
        type: 'group',
        title: 'Illegal Activities',
        blocks: [{ type: 'paragraph', text: 'Use the Services in violation of any law, regulation, or governmental requirement.' }],
      },
      {
        type: 'group',
        title: 'Security Violations',
        blocks: [
          { type: 'paragraph', text: 'Attempt to:' },
          {
            type: 'list',
            items: [
              'Access systems without authorization',
              'Circumvent security controls',
              'Probe, scan, or test vulnerabilities',
              'Interfere with platform operations',
            ],
          },
        ],
      },
      {
        type: 'group',
        title: 'Harmful Content',
        blocks: [
          { type: 'paragraph', text: 'Upload, transmit, or distribute:' },
          { type: 'list', items: ['Malware', 'Viruses', 'Spyware', 'Malicious scripts', 'Harmful code'] },
        ],
      },
      {
        type: 'group',
        title: 'Misrepresentation',
        blocks: [{ type: 'paragraph', text: 'Impersonate individuals, organizations, or falsely represent affiliations.' }],
      },
      {
        type: 'group',
        title: 'Automated Abuse',
        blocks: [{ type: 'paragraph', text: 'Use bots, scrapers, crawlers, or automated systems that negatively impact platform performance.' }],
      },
      {
        type: 'group',
        title: 'Sensitive Information',
        blocks: [
          {
            type: 'paragraph',
            text: 'Upload or connect highly sensitive personal information unless expressly supported and authorized by AppGallop.',
          },
        ],
      },
    ],
  },
  {
    title: '10. User Content',
    blocks: [
      { type: 'paragraph', text: 'Certain Services allow users to submit content, including:' },
      { type: 'list', items: ['Comments', 'Reviews', 'Profiles', 'Documents', 'Images', 'Communications'] },
      { type: 'paragraph', text: 'You retain ownership of your content.' },
      {
        type: 'paragraph',
        text: 'By submitting content to the Services, you grant AppGallop a worldwide, non-exclusive, royalty-free license to host, store, reproduce, modify, display, distribute, and process such content solely for the purpose of operating, improving, securing, and promoting the Services.',
      },
      { type: 'paragraph', text: 'You represent that:' },
      {
        type: 'list',
        items: [
          'You own or have rights to the content submitted.',
          'Your content does not violate any laws.',
          'Your content does not infringe third-party rights.',
        ],
      },
    ],
  },
  {
    title: '11. Intellectual Property',
    blocks: [
      {
        type: 'paragraph',
        text: 'The Services and all related materials, including software, designs, trademarks, logos, text, graphics, interfaces, and proprietary technology, are owned by AppGallop or its licensors.',
      },
      { type: 'paragraph', text: 'Nothing in these Terms transfers ownership of intellectual property rights to you.' },
      { type: 'paragraph', text: 'You may not:' },
      { type: 'list', items: ['Copy', 'Reproduce', 'Modify', 'Reverse engineer', 'Distribute', 'Sell', 'Create derivative works'] },
      { type: 'paragraph', text: 'without prior written consent.' },
    ],
  },
  {
    title: '12. API & Integration Services',
    blocks: [
      { type: 'paragraph', text: 'Where AppGallop provides APIs, integrations, connectors, or automation services:' },
      {
        type: 'list',
        items: [
          'You remain responsible for connected data sources.',
          'You authorize AppGallop to process data necessary to provide functionality.',
          'You must comply with all applicable third-party platform requirements.',
        ],
      },
      { type: 'paragraph', text: 'AppGallop may suspend API access if usage threatens platform security, stability, or compliance obligations.' },
    ],
  },
  {
    title: '13. Privacy & Data Protection',
    blocks: [
      { type: 'paragraph', text: 'Your use of the Services is subject to our Privacy Policy.' },
      {
        type: 'paragraph',
        text: 'By using the Services, you acknowledge that AppGallop may collect, process, store, and transfer information as described in the Privacy Policy.',
      },
    ],
  },
  {
    title: '14. Service Availability',
    blocks: [
      { type: 'paragraph', text: 'We continuously improve our Services and may:' },
      {
        type: 'list',
        items: ['Add features', 'Remove features', 'Modify functionality', 'Update infrastructure', 'Suspend components for maintenance'],
      },
      { type: 'paragraph', text: 'We do not guarantee uninterrupted or error-free availability.' },
    ],
  },
  {
    title: '15. Suspension & Termination',
    blocks: [
      { type: 'paragraph', text: 'AppGallop may suspend or terminate access if:' },
      {
        type: 'list',
        items: [
          'These Terms are violated.',
          'Fraudulent activity is suspected.',
          'Security risks arise.',
          'Payment obligations remain outstanding.',
          'Legal or regulatory obligations require action.',
        ],
      },
      { type: 'paragraph', text: 'Upon termination:' },
      {
        type: 'list',
        items: ['Access rights immediately cease.', 'Certain information may be retained as required by law.', 'Accrued obligations remain enforceable.'],
      },
    ],
  },
  {
    title: '16. Indemnification',
    blocks: [
      {
        type: 'paragraph',
        text: 'You agree to defend, indemnify, and hold harmless AppGallop, its affiliates, officers, employees, partners, and licensors from any claims, damages, liabilities, losses, costs, or expenses arising from:',
      },
      {
        type: 'list',
        items: ['Your use of the Services', 'Violation of these Terms', 'Violation of applicable laws', 'Infringement of third-party rights'],
      },
    ],
  },
  {
    title: '17. Disclaimer of Warranties',
    blocks: [
      { type: 'paragraph', text: 'THE SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS.' },
      { type: 'paragraph', text: 'TO THE MAXIMUM EXTENT PERMITTED BY LAW, APPGALLOP DISCLAIMS ALL WARRANTIES, INCLUDING:' },
      {
        type: 'list',
        items: ['MERCHANTABILITY', 'FITNESS FOR A PARTICULAR PURPOSE', 'NON-INFRINGEMENT', 'AVAILABILITY', 'ACCURACY', 'RELIABILITY'],
      },
      { type: 'paragraph', text: 'APPGALLOP DOES NOT GUARANTEE THAT THE SERVICES WILL BE ERROR-FREE, SECURE, OR UNINTERRUPTED.' },
    ],
  },
  {
    title: '18. Limitation of Liability',
    blocks: [
      { type: 'paragraph', text: 'TO THE MAXIMUM EXTENT PERMITTED BY LAW, APPGALLOP SHALL NOT BE LIABLE FOR:' },
      {
        type: 'list',
        items: ['INDIRECT DAMAGES', 'INCIDENTAL DAMAGES', 'CONSEQUENTIAL DAMAGES', 'LOSS OF PROFITS', 'LOSS OF BUSINESS', 'LOSS OF DATA', 'BUSINESS INTERRUPTION'],
      },
      {
        type: 'paragraph',
        text: "IN NO EVENT SHALL APPGALLOP'S TOTAL LIABILITY EXCEED THE AMOUNT PAID BY YOU TO APPGALLOP DURING THE TWELVE (12) MONTHS PRECEDING THE CLAIM.",
      },
    ],
  },
  {
    title: '19. Governing Law',
    blocks: [
      { type: 'paragraph', text: 'These Terms shall be governed by and construed in accordance with the laws of India, without regard to conflict of law principles.' },
      { type: 'paragraph', text: 'Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts located in New Delhi, India.' },
    ],
  },
  {
    title: '20. Changes to These Terms',
    blocks: [
      { type: 'paragraph', text: 'We may update these Terms from time to time.' },
      { type: 'paragraph', text: 'When material changes occur, we will provide reasonable notice through:' },
      { type: 'list', items: ['Email', 'Platform notifications', 'Website announcements'] },
      { type: 'paragraph', text: 'Continued use of the Services following such updates constitutes acceptance of the revised Terms.' },
    ],
  },
  {
    title: '21. Contact Information',
    blocks: [
      { type: 'paragraph', text: 'For questions regarding these Terms, please contact:' },
      { type: 'paragraph', text: 'AppGallop Pvt. Ltd.' },
      { type: 'paragraph', text: 'Email: legal@appgallop.com' },
      { type: 'paragraph', text: 'Email: support@appgallop.com' },
      { type: 'paragraph', text: 'Website: www.appgallop.com' },
    ],
  },
];

const ContentRenderer = ({ blocks }: { blocks: ContentBlock[] }) => (
  <div className="space-y-5">
    {blocks.map((block, index) => {
      if (block.type === 'paragraph') {
        return (
          <p key={index} className="text-sm font-medium leading-7 text-slate-600">
            {block.text}
          </p>
        );
      }

      if (block.type === 'list') {
        return (
          <ul key={index} className="grid gap-2 pl-5 text-sm font-medium leading-7 text-slate-600">
            {block.items.map((item) => (
              <li key={item} className="list-disc marker:text-[#ff5d22]">
                {item}
              </li>
            ))}
          </ul>
        );
      }

      return (
        <div key={index} className="space-y-3">
          <h3 className="text-base font-black text-slate-950">{block.title}</h3>
          <ContentRenderer blocks={block.blocks} />
        </div>
      );
    })}
  </div>
);

export default function TermsConditionsPage() {
  return (
    <main className="bg-white pt-28 text-slate-950">
      <section className="mx-auto max-w-4xl px-6 pb-16 md:px-10 md:pb-24">
        <div className="mb-10">
          <p className="mb-4 text-[10px] font-black uppercase tracking-widest text-[#ff5d22]">
            AppGallop
          </p>
          <h1 className="text-4xl font-black tracking-tight md:text-5xl">Terms of Service</h1>
          <p className="mt-4 text-sm font-semibold leading-7 text-slate-500">
            Effective Date: June 15, 2026
          </p>
        </div>

        <div className="space-y-12">
          {sections.map((section) => (
            <article key={section.title} className="border-t border-slate-200 pt-8">
              <h2 className="mb-5 text-2xl font-black tracking-tight text-slate-950">
                {section.title}
              </h2>
              <ContentRenderer blocks={section.blocks} />
            </article>
          ))}
        </div>

        <div className="mt-12 border-t border-slate-200 pt-6">
          <Link
            to="/"
            className="inline-flex min-h-11 items-center rounded-lg bg-[#ff5d22] px-5 text-[10px] font-black uppercase tracking-widest text-white transition-colors hover:bg-slate-950"
          >
            Back to Home
          </Link>
        </div>
      </section>
    </main>
  );
}
